import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FaClock, FaPlus, FaTrash, FaCalendarAlt } from 'react-icons/fa';
import { TimeFrame } from '../../../../../types';
import './TimeframeConfiguration.scss';
import TimePicker from './components/TimePicker/TimePicker';

interface TimeframeConfigurationProps {
  alwaysActive: boolean;
  setAlwaysActive: (value: boolean) => void;
  timeframes: TimeFrame[];
  setTimeframes: (timeframes: TimeFrame[]) => void;
}

const DAYS_OF_WEEK = [
  { value: 1, label: 'L', fullName: 'Lunes' },
  { value: 2, label: 'M', fullName: 'Martes' },
  { value: 3, label: 'X', fullName: 'Miércoles' },
  { value: 4, label: 'J', fullName: 'Jueves' },
  { value: 5, label: 'V', fullName: 'Viernes' },
  { value: 6, label: 'S', fullName: 'Sábado' },
  { value: 7, label: 'D', fullName: 'Domingo' }
];

const TimeframeConfiguration: React.FC<TimeframeConfigurationProps> = ({
  alwaysActive,
  setAlwaysActive,
  timeframes,
  setTimeframes
}) => {
  const [expandedFrameIndex, setExpandedFrameIndex] = useState<number | null>(null);

  const handleAddTimeframe = () => {
    const newFrame: TimeFrame = {
      name: `Horario ${timeframes.length + 1}`,
      days: [1, 2, 3, 4, 5], // Default to weekdays
      start_time: '09:00',
      end_time: '17:00',
      active: true
    };
    setTimeframes([...timeframes, newFrame]);
    setExpandedFrameIndex(timeframes.length);
  };

  const handleRemoveTimeframe = (index: number) => {
    const newFrames = timeframes.filter((_, i) => i !== index);
    setTimeframes(newFrames);
    if (expandedFrameIndex === index) {
      setExpandedFrameIndex(null);
    }
  };

  const handleUpdateTimeframe = (index: number, updates: Partial<TimeFrame>) => {
    const newFrames = [...timeframes];
    newFrames[index] = { ...newFrames[index], ...updates };
    setTimeframes(newFrames);
  };

  const handleDayToggle = (frameIndex: number, day: number) => {
    const frame = timeframes[frameIndex];
    const newDays = frame.days.includes(day)
      ? frame.days.filter(d => d !== day)
      : [...frame.days, day].sort((a, b) => a - b);
    handleUpdateTimeframe(frameIndex, { days: newDays });
  };

  const toggleAllDays = (frameIndex: number) => {
    const frame = timeframes[frameIndex];
    const allSelected = frame.days.length === 7;
    handleUpdateTimeframe(frameIndex, {
      days: allSelected ? [] : [1, 2, 3, 4, 5, 6, 7]
    });
  };

  const selectWeekdays = (frameIndex: number) => {
    handleUpdateTimeframe(frameIndex, { days: [1, 2, 3, 4, 5] });
  };

  const selectWeekend = (frameIndex: number) => {
    handleUpdateTimeframe(frameIndex, { days: [6, 7] });
  };

  const getDaysLabel = (days: number[]) => {
    if (days.length === 0) return 'Ningún día';
    if (days.length === 7) return 'Todos los días';
    if (JSON.stringify([...days].sort()) === JSON.stringify([1, 2, 3, 4, 5])) return 'Entre semana';
    if (JSON.stringify([...days].sort()) === JSON.stringify([6, 7])) return 'Fines de semana';
    return days.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label).join(', ');
  };

  return (
    <div className="form-section timeframe-section">
      <div className="section-title">
        <FaClock className="section-icon" />
        Horarios de Disponibilidad
      </div>

      {/* Always Active Toggle */}
      <div className="always-active-toggle">
        <div className="custom-switch">
          <Form.Check
            type="switch"
            id="always-active-switch"
            checked={alwaysActive}
            onChange={(e) => setAlwaysActive(e.target.checked)}
          />
          <div>
            <Form.Label htmlFor="always-active-switch" className="mb-0">
              Descuento siempre activo (24/7)
            </Form.Label>
            <div className="toggle-description">
              {alwaysActive
                ? 'Este descuento estará disponible en todo momento'
                : 'Configura horarios específicos para cuando el descuento esté activo'}
            </div>
          </div>
        </div>
      </div>

      {/* Timeframes Configuration */}
      {!alwaysActive && (
        <div className="timeframes-container">
          {timeframes.length === 0 ? (
            <div className="no-timeframes">
              <FaCalendarAlt className="empty-icon" />
              <p>No hay horarios configurados</p>
              <p className="empty-hint">Agrega al menos un horario para activar el descuento</p>
            </div>
          ) : (
            <div className="timeframes-list">
              {timeframes.map((frame, index) => (
                <div key={index} className="timeframe-card">
                  <div
                    className="timeframe-header"
                    onClick={() => setExpandedFrameIndex(expandedFrameIndex === index ? null : index)}
                  >
                    <div className="timeframe-summary">
                      <div className="frame-name">{frame.name}</div>
                      <div className="frame-quick-info">
                        <span className="days-badge">{getDaysLabel(frame.days)}</span>
                        <span className="time-badge">
                          {frame.start_time} - {frame.end_time}
                        </span>
                      </div>
                    </div>
                    <div className="timeframe-actions">
                      <Button
                        variant="link"
                        className="delete-frame-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveTimeframe(index);
                        }}
                        title="Eliminar horario"
                      >
                        <FaTrash />
                      </Button>
                      <span className="expand-icon">
                        {expandedFrameIndex === index ? '−' : '+'}
                      </span>
                    </div>
                  </div>

                  {expandedFrameIndex === index && (
                    <div className="timeframe-body">
                      {/* Frame Name */}
                      <Form.Group className="mb-3">
                        <Form.Label>Nombre del horario</Form.Label>
                        <Form.Control
                          type="text"
                          value={frame.name}
                          onChange={(e) => handleUpdateTimeframe(index, { name: e.target.value })}
                          placeholder="Ej: Almuerzo, Happy Hour, Noche"
                        />
                      </Form.Group>

                      {/* Days Selection */}
                      <Form.Group className="mb-3">
                        <Form.Label>Días activos</Form.Label>
                        <div className="days-quick-select">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => selectWeekdays(index)}
                          >
                            Entre semana
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => selectWeekend(index)}
                          >
                            Fin de semana
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => toggleAllDays(index)}
                          >
                            {frame.days.length === 7 ? 'Ninguno' : 'Todos'}
                          </Button>
                        </div>
                        <div className="days-selector">
                          {DAYS_OF_WEEK.map((day) => (
                            <div
                              key={day.value}
                              className={`day-button ${frame.days.includes(day.value) ? 'selected' : ''}`}
                              onClick={() => handleDayToggle(index, day.value)}
                              title={day.fullName}
                            >
                              {day.label}
                            </div>
                          ))}
                        </div>
                      </Form.Group>

                      {/* Time Range */}
                      <div className="time-range-group">
                        <Form.Group className="time-input-group">
                          <Form.Label>Hora de inicio</Form.Label>
                          <div className="time-input-wrapper custom-picker">
                            <TimePicker
                              value={frame.start_time}
                              onChange={(val) => handleUpdateTimeframe(index, { start_time: val })}
                              minuteStep={5}
                              ariaLabel={`Hora de inicio ${frame.name}`}
                            />
                          </div>
                        </Form.Group>
                        <div className="time-separator">→</div>
                        <Form.Group className="time-input-group">
                          <Form.Label>Hora de fin</Form.Label>
                          <div className="time-input-wrapper custom-picker">
                            <TimePicker
                              value={frame.end_time}
                              onChange={(val) => handleUpdateTimeframe(index, { end_time: val })}
                              minuteStep={5}
                              ariaLabel={`Hora de fin ${frame.name}`}
                            />
                          </div>
                        </Form.Group>
                      </div>

                      {/* Overnight indicator */}
                      {frame.start_time > frame.end_time && (
                        <div className="overnight-notice">
                          <FaClock /> Este horario cruza la medianoche (continúa al día siguiente)
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add Timeframe Button */}
          <Button
            variant="outline-primary"
            className="add-timeframe-btn"
            onClick={handleAddTimeframe}
          >
            <FaPlus /> Agregar Horario
          </Button>
        </div>
      )}
    </div>
  );
};

export default TimeframeConfiguration;
